import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Coins, Loader2, CheckCircle, AlertCircle, DollarSign, Gift, Crown, Sparkles } from "lucide-react";
import { authAxios } from "../utils/auth";
import SpinningCoin from "./rewards/SpinningCoin";

// Module-level cache for /admin/plog/c2c. Every open of the coin modal, the
// plan cards, and the top-up page previously refetched the same value —
// combined with the generic 100/min rate-limit bucket, that was surfacing
// as an intermittent "Failed to load conversion rate" toast. The value only
// changes when an admin updates it, so a 5-minute per-tab cache is safe.
const _C2C_TTL_MS = 5 * 60 * 1000;
let _c2cCache = { at: 0, data: null };
let _c2cInflight = null;
const fetchC2cCached = async () => {
  if (_c2cCache.data && Date.now() - _c2cCache.at < _C2C_TTL_MS) {
    return _c2cCache.data;
  }
  if (_c2cInflight) return _c2cInflight;
  _c2cInflight = (async () => {
    try {
      const res = await authAxios.get("/admin/plog/c2c");
      _c2cCache = { at: Date.now(), data: res.data };
      return res.data;
    } finally {
      _c2cInflight = null;
    }
  })();
  return _c2cInflight;
};

/**
 * Coin Purchase Modal
 * Allows users to purchase coins based on USD price options with bonus tiers
 */
const CoinPurchaseModal = ({ 
  isOpen, 
  onClose, 
  currentBalance, 
  onPurchaseComplete,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [calculatedCoins, setCalculatedCoins] = useState(null);
  const [baseCoins, setBaseCoins] = useState(null);
  const [bonusPercent, setBonusPercent] = useState(0);
  const [usdCrc, setUsdCrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [fetchingRate, setFetchingRate] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [newBalance, setNewBalance] = useState(null);
  const [userRole, setUserRole] = useState("user");
  // Payment method: "card" (Stripe) | "affiliate_balance"
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [availableAffCents, setAvailableAffCents] = useState(0);
  const [fetchingAff, setFetchingAff] = useState(false);
  const [payingWithBalance, setPayingWithBalance] = useState(false);
  // Tally displayed on success for both card and affiliate-balance purchases
  const [purchaseCoins, setPurchaseCoins] = useState(0);
  const [purchaseBonusCoins, setPurchaseBonusCoins] = useState(0);
  const [spinTick, setSpinTick] = useState(0);
  // Live Subscribe bonus offer (sponsor or system-wide, whichever is larger).
  // Drives the "Limited Offer" CTA bar under the tabs and the gold-pulse on
  // the Subscribe tab. Null when no active offer / user is on yearly.
  const [subscribeOffer, setSubscribeOffer] = useState(null);
  // User's current billing cycle — if yearly, they can't claim the offer so
  // we suppress both the CTA bar and the tab-pulse.
  const [userBillingCycle, setUserBillingCycle] = useState(null);

  // Price options with bonus percentages
  const priceOptions = [
    { price: 25, bonus: 0 },
    { price: 50, bonus: 10 },
    { price: 75, bonus: 20 },
    { price: 100, bonus: 35 }
  ];

  // Fetch USD to CRC rate + role + affiliate balance when modal opens
  useEffect(() => {
    if (isOpen || embedded) {
      fetchUsdCrc();
      authAxios.get("/auth/me").then((r) => {
        setUserRole(r?.data?.role || "user");
        setUserBillingCycle(r?.data?.subscription_billing_cycle || null);
      }).catch(() => {
        setUserRole("user");
        setUserBillingCycle(null);
      });
      fetchAffiliateBalance();
      // Load the active Subscribe bonus offer. We swallow errors and 21-day
      // cooldown / no-offer states are represented as {offer: null}.
      authAxios.get("/affiliate/sponsor-subscribe-offer").then((r) => {
        setSubscribeOffer(r?.data?.offer || null);
      }).catch(() => setSubscribeOffer(null));
    }
  }, [isOpen, embedded]);

  // Yearly-subscribers cannot claim the Subscribe bonus — hide the CTA + pulse.
  const offerEligible = Boolean(subscribeOffer) && userBillingCycle !== "yearly";
  const offerMaxBonusCoins = offerEligible
    ? (subscribeOffer?.plan_bonuses?.yearly?.PLATINUM || subscribeOffer?.base_bonus || 0)
    : 0;

  const fetchAffiliateBalance = async () => {
    setFetchingAff(true);
    try {
      const res = await authAxios.get("/affiliate/balance");
      setAvailableAffCents(Number(res?.data?.available_cents) || 0);
    } catch (_e) {
      setAvailableAffCents(0);
    } finally {
      setFetchingAff(false);
    }
  };

  const fetchUsdCrc = async (attempt = 0) => {
    setFetchingRate(true);
    try {
      const data = await fetchC2cCached();
      setUsdCrc(data?.usd_crc);
      setError((prev) => (prev === "Failed to load conversion rate. Please try again." ? "" : prev));
    } catch (err) {
      // Transient 5xx / network flap → auto-retry once after 700ms before
      // surfacing the error banner. The upstream rate-limiter occasionally
      // times out during its Redis-→-memory fallback.
      if (attempt < 1 && (!err?.response || err.response.status >= 500)) {
        setTimeout(() => fetchUsdCrc(attempt + 1), 700);
        return;
      }
      console.error("Failed to fetch USD to CRC rate:", err);
      setError("Failed to load conversion rate. Please try again.");
    } finally {
      if (attempt >= 1 || !fetchingRate) setFetchingRate(false);
    }
  };

  const handlePriceSelect = (price, bonus) => {
    setSelectedPrice(price);
    setBonusPercent(bonus);
    setError("");
    
    if (usdCrc && usdCrc > 0) {
      const base = Math.floor(price * usdCrc);
      const bonusCoins = Math.floor(base * (bonus / 100));
      const total = base + bonusCoins;
      setBaseCoins(base);
      setCalculatedCoins(total);
    } else {
      setError("USD to CRC rate not configured. Please contact admin.");
      setBaseCoins(null);
      setCalculatedCoins(null);
    }
  };

  const handleAffiliateBalancePurchase = async () => {
    if (!selectedPrice) {
      setError("Please select a purchase amount");
      return;
    }
    const packId = `coins_${selectedPrice}`;
    const requiredCents = selectedPrice * 100;
    if (availableAffCents < requiredCents) {
      setError(`Insufficient Affiliate Balance. Available: $${(availableAffCents / 100).toFixed(2)}`);
      return;
    }
    setPayingWithBalance(true);
    setError("");
    try {
      const res = await authAxios.post("/affiliate/coin-pack/purchase-with-balance", {
        pack_id: packId,
      });
      const credited = Number(res?.data?.coins_credited || 0);
      // Best-effort split: backend returns coins_credited (total). Reuse the
      // client-side calc for display; base/bonus already computed by
      // handlePriceSelect via priceOptions.
      const base = baseCoins || credited;
      const bonus = Math.max(0, credited - base);
      setPurchaseCoins(base);
      setPurchaseBonusCoins(bonus);
      setNewBalance((currentBalance || 0) + credited);
      setAvailableAffCents(Number(res?.data?.new_available_cents) || 0);
      setSuccess(true);
      setSpinTick((t) => t + 1);
      if (onPurchaseComplete) {
        onPurchaseComplete((currentBalance || 0) + credited);
      }
      setTimeout(() => handleClose(), 3500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Purchase failed. Please try again.");
    } finally {
      setPayingWithBalance(false);
    }
  };

  const handleStripeCheckout = async () => {
    if (!selectedPrice) {
      setError("Please select a purchase amount");
      return;
    }
    const packId = `coins_${selectedPrice}`;
    setRedirecting(true);
    setError("");
    try {
      // Remember where the modal was opened from so the /payment/success
      // page can return the user to the same location on CONTINUE. When
      // embedded on /plans, /plans itself has already stored the correct
      // "return to" (the page that opened /plans), so we don't overwrite it.
      if (!embedded) {
        try {
          sessionStorage.setItem(
            "agent_eros_payment_return_to",
            window.location.pathname + window.location.search
          );
        } catch (_) { /* private mode / storage disabled */ }
      }
      const res = await authAxios.post("/stripe/checkout/coin-pack", {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      if (res?.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setError("Checkout could not be started. Please try again.");
    } catch (err) {
      console.error("Stripe checkout failed:", err);
      setError(err.response?.data?.detail || "Failed to start checkout. Please try again.");
    } finally {
      setRedirecting(false);
    }
  };

  const handlePurchase = async () => {
    if (!calculatedCoins || calculatedCoins <= 0) {
      setError("Please select a price option");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    // Calculate actual bonus coins given
    const actualBonusCoins = calculatedCoins - baseCoins;

    try {
      const response = await authAxios.post("/credits/add", {
        amount: calculatedCoins,
        transaction_type: "TEST_CR",
        reference_id: `coin_purchase_modal_$${selectedPrice}`,
        // Bonus pool tracking
        base_coins: baseCoins,
        bonus_coins: actualBonusCoins > 0 ? actualBonusCoins : 0,
        price_usd: selectedPrice
      });

      setNewBalance(response.data.new_balance);
      setPurchaseCoins(baseCoins || 0);
      setPurchaseBonusCoins(actualBonusCoins > 0 ? actualBonusCoins : 0);
      setSuccess(true);
      setSpinTick((t) => t + 1);

      // Notify parent to refresh balance
      if (onPurchaseComplete) {
        onPurchaseComplete(response.data.new_balance);
      }

      // Auto-close after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      console.error("Purchase failed:", err);
      setError(err.response?.data?.detail || "Failed to add coins. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPrice(null);
    setCalculatedCoins(null);
    setBaseCoins(null);
    setBonusPercent(0);
    setError("");
    setSuccess(false);
    setNewBalance(null);
    // Embedded on /plans: no wrapping modal to close — skip onClose so we
    // don't blow up when the parent didn't pass it. This is what the
    // "TEST PURCHASE" admin button was crashing on. Non-embedded use
    // (legacy modal invocations) still calls onClose() to dismiss.
    if (!embedded && typeof onClose === "function") {
      onClose();
    }
  };

  // Calculate coins for display in price buttons
  const getCoinsForPrice = (price, bonus) => {
    if (!usdCrc) return { base: 0, total: 0 };
    const base = Math.floor(price * usdCrc);
    const bonusCoins = Math.floor(base * (bonus / 100));
    return { base, total: base + bonusCoins };
  };

  // Shared body renderer (balance hero + form) used by both modal and embedded modes.
  const renderBody = () => (
    <>
      <div className="bg-white/5 rounded-lg p-4 border border-white/10 flex items-center justify-between gap-3" data-testid="balance-hero">
        {success ? (
          <div className="flex-1 min-w-0" data-testid="purchase-tally">
            <div className="text-green-300 font-medium text-sm mb-2 flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4" /> Purchase Successful!
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-white/60"><span>Previous balance</span><span className="font-mono">{(currentBalance || 0).toLocaleString()}</span></div>
              <div className="flex justify-between text-amber-300"><span>+ Coins purchased</span><span className="font-mono">{purchaseCoins.toLocaleString()}</span></div>
              {purchaseBonusCoins > 0 && (
                <div className="flex justify-between text-emerald-300"><span>+ Bonus coins</span><span className="font-mono">{purchaseBonusCoins.toLocaleString()}</span></div>
              )}
              <div className="flex justify-between text-white font-bold pt-1 border-t border-white/10"><span>New balance</span><span className="font-mono text-amber-400 text-lg">{(newBalance || 0).toLocaleString()}</span></div>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-white/60 text-sm mb-1">Current Balance</div>
            <div className="text-2xl font-bold text-amber-400">{currentBalance !== null ? currentBalance.toLocaleString() : "—"} coins</div>
          </div>
        )}
        <SpinningCoin size={success ? 84 : 64} spinTrigger={success ? spinTick : (selectedPrice || 0)} onClickToSpin={true} className="flex-shrink-0" />
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {!success && (
        fetchingRate ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 text-divine-gold animate-spin mr-2" />
            <span className="text-white/60">Loading rates...</span>
          </div>
        ) : (
          <>
            <div>
              <label className="text-white/70 text-sm mb-2 block">Payment Method</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => { setPaymentMethod("card"); setError(""); }}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${paymentMethod === "card" ? "border-amber-500 bg-amber-500/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                  data-testid="payment-method-card">
                  <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-400" /><span className="text-sm text-white font-medium">Card</span></div>
                  <div className="text-[11px] text-white/50 mt-1">Pay via Stripe</div>
                </button>
                <button type="button" onClick={() => { setPaymentMethod("affiliate_balance"); setError(""); }}
                  disabled={availableAffCents <= 0 && !fetchingAff}
                  className={`p-3 rounded-lg border-2 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${paymentMethod === "affiliate_balance" ? "border-amber-500 bg-amber-500/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                  data-testid="payment-method-affiliate-balance">
                  <div className="flex items-center gap-2"><Gift className="w-4 h-4 text-amber-400" /><span className="text-sm text-white font-medium">Affiliate Balance</span></div>
                  <div className="text-[11px] text-white/50 mt-1" data-testid="affiliate-balance-available">
                    {fetchingAff ? "…" : `$${(availableAffCents / 100).toFixed(2)} available`}
                  </div>
                </button>
              </div>
              {paymentMethod === "affiliate_balance" && selectedPrice && (
                <div className="mt-2 text-[11px] text-white/50 flex items-center justify-between">
                  <span>Balance after purchase:</span>
                  <span className={`font-mono ${availableAffCents >= selectedPrice * 100 ? "text-amber-400" : "text-red-400"}`}>
                    ${((availableAffCents - selectedPrice * 100) / 100).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="text-white/70 text-sm mb-3 block">Select a purchase amount</label>
              <div className="grid grid-cols-2 gap-3">
                {priceOptions.map(({ price, bonus }) => {
                  const { base, total } = getCoinsForPrice(price, bonus);
                  const isSelected = selectedPrice === price;
                  return (
                    <button key={price} onClick={() => handlePriceSelect(price, bonus)} disabled={loading || !usdCrc}
                      className={`relative px-4 py-4 rounded-lg border-2 transition-all ${isSelected ? 'border-amber-500 bg-amber-500/20' : 'border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10'} ${(!usdCrc || loading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`price-btn-${price}`}>
                      {bonus > 0 && (
                        <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-700 text-white text-xs font-bold rounded-full flex items-center gap-1">
                          <Gift className="w-3 h-3" />+{bonus}%
                        </div>
                      )}
                      <div className="flex items-center justify-center gap-1">
                        <DollarSign className={`w-5 h-5 ${isSelected ? 'text-amber-400' : 'text-white/60'}`} />
                        <span className={`text-2xl font-bold ${isSelected ? 'text-amber-400' : 'text-white'}`}>{price}</span>
                      </div>
                      {usdCrc && (
                        <div className="mt-1">
                          {bonus > 0 ? (
                            <div className="flex flex-col items-center">
                              <span className="text-xs text-white/40 line-through">{base.toLocaleString()}</span>
                              <span className="text-sm text-green-400 font-medium">{total.toLocaleString()} coins</span>
                            </div>
                          ) : (
                            <div className="text-xs text-white/40">{total.toLocaleString()} coins</div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedPrice && calculatedCoins && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white/60 text-sm">You will receive</div>
                    <div className="flex items-center gap-2">
                      {bonusPercent > 0 && (<span className="text-lg text-white/40 line-through">{baseCoins?.toLocaleString()}</span>)}
                      <span className="text-2xl font-bold text-amber-400">{calculatedCoins.toLocaleString()} coins</span>
                    </div>
                    {bonusPercent > 0 && (
                      <div className="flex items-center gap-1 text-green-400 text-sm mt-1">
                        <Gift className="w-4 h-4" /><span>+{bonusPercent}% bonus included!</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-white/60 text-sm">Price</div>
                    <div className="text-xl font-bold text-white">${selectedPrice}</div>
                  </div>
                </div>
              </div>
            )}

            {!fetchingRate && !usdCrc && (
              <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-300 text-sm">USD to CRC rate not configured. Please set it in Admin → Tokens.</span>
              </div>
            )}
          </>
        )
      )}
    </>
  );

  // Embedded mode — used by /plans "Top-up" tab. Renders the panel content
  // without the Dialog wrapper + tab bar (the parent page owns those).
  if (embedded) {
    return (
      <div className="max-w-md mx-auto" data-testid="coin-topup-panel">
        {/* Limited Offer CTA */}
        {offerEligible && offerMaxBonusCoins > 0 && (
          <div
            className="rounded-lg mb-4 px-4 py-2.5 border border-divine-gold/20 bg-gradient-to-r from-divine-gold/[0.14] via-divine-gold/[0.08] to-transparent"
            data-testid="limited-offer-cta"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-2.5">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-divine-gold" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-divine-gold">Limited Offer — MAX Coin Bonus</div>
                <div className="text-xs text-white/70 leading-snug mt-0.5">
                  Subscribe now and unlock up to{" "}
                  <span className="font-mono font-semibold text-amber-300 limited-offer-count-pulse" data-testid="limited-offer-bonus-count">
                    {offerMaxBonusCoins.toLocaleString()}
                  </span>{" "}
                  bonus coins.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="py-4 space-y-4">
          {renderBody()}
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-white/[0.06]">
          {!success && (
            paymentMethod === "affiliate_balance" ? (
              <Button
                onClick={handleAffiliateBalancePurchase}
                disabled={payingWithBalance || !selectedPrice || fetchingRate ||
                          (selectedPrice && availableAffCents < selectedPrice * 100)}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                data-testid="pay-with-affiliate-balance-btn"
              >
                {payingWithBalance ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>) : (<><Gift className="w-4 h-4 mr-2" />Pay ${selectedPrice || "…"} from Affiliate Balance</>)}
              </Button>
            ) : (
              <Button
                onClick={handleStripeCheckout}
                disabled={redirecting || loading || !selectedPrice || fetchingRate}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                data-testid="stripe-checkout-btn"
              >
                {redirecting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting to Stripe…</>) : (<><DollarSign className="w-4 h-4 mr-2" />Pay ${selectedPrice || "…"} with Stripe</>)}
              </Button>
            )
          )}
          {!success && (userRole === "admin" || userRole === "support") && (
            <Button
              onClick={handlePurchase}
              disabled={loading || redirecting || !calculatedCoins || fetchingRate}
              variant="outline"
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
              data-testid="test-purchase-btn"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>) : (<><Coins className="w-4 h-4 mr-2" />TEST</>)}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-divine-charcoal border-divine-gold/30 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-cinzel text-divine-gold">
            <Coins className="w-5 h-5" />
            Coin Purchase
          </DialogTitle>
        </DialogHeader>

        {/* Tab: Topup (current) | Subscribe (navigates to /plans) */}
        <div className="flex items-center gap-1 border-b border-white/[0.06] -mx-6 px-6" data-testid="purchase-tabs">
          <button
            type="button"
            className="px-3 py-2 text-sm font-medium border-b-2 border-divine-gold text-divine-gold flex items-center gap-1.5"
            data-testid="tab-topup"
          >
            <Coins className="w-3.5 h-3.5" /> Top-up
          </button>
          <button
            type="button"
            onClick={() => {
              handleClose();
              navigate("/plans", { state: { from: location.pathname } });
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 border-transparent text-white/50 hover:text-white transition flex items-center gap-1.5 rounded-t-md ${
              offerEligible ? "subscribe-tab-pulse" : ""
            }`}
            data-testid="tab-plans"
          >
            <Crown className="w-3.5 h-3.5" /> Subscribe
          </button>
        </div>

        {/* Limited Offer CTA — appears when a Subscribe bonus offer is active
            and the user is not already on a yearly plan. Non-interactive on
            purpose: the Subscribe tab directly above is the affordance. */}
        {offerEligible && offerMaxBonusCoins > 0 && (
          <div
            className="-mx-6 px-6 py-2.5 border-b border-divine-gold/20 bg-gradient-to-r from-divine-gold/[0.14] via-divine-gold/[0.08] to-transparent"
            data-testid="limited-offer-cta"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-2.5">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-divine-gold" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-divine-gold">
                  Limited Offer — MAX Coin Bonus
                </div>
                <div className="text-xs text-white/70 leading-snug mt-0.5">
                  Subscribe now and unlock up to{" "}
                  <span
                    className="font-mono font-semibold text-amber-300 limited-offer-count-pulse"
                    data-testid="limited-offer-bonus-count"
                  >
                    {offerMaxBonusCoins.toLocaleString()}
                  </span>{" "}
                  bonus coins.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="py-4 space-y-4">
          {/* Balance + Spinning coin. During success we show a richer tally. */}
          <div className="bg-white/5 rounded-lg p-4 border border-white/10 flex items-center justify-between gap-3" data-testid="balance-hero">
            {success ? (
              <div className="flex-1 min-w-0" data-testid="purchase-tally">
                <div className="text-green-300 font-medium text-sm mb-2 flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4" /> Purchase Successful!
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-white/60">
                    <span>Previous balance</span>
                    <span className="font-mono">{(currentBalance || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-amber-300">
                    <span>+ Coins purchased</span>
                    <span className="font-mono">{purchaseCoins.toLocaleString()}</span>
                  </div>
                  {purchaseBonusCoins > 0 && (
                    <div className="flex justify-between text-emerald-300">
                      <span>+ Bonus coins</span>
                      <span className="font-mono">{purchaseBonusCoins.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-white font-bold pt-1 border-t border-white/10">
                    <span>New balance</span>
                    <span className="font-mono text-amber-400 text-lg">{(newBalance || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-white/60 text-sm mb-1">Current Balance</div>
                <div className="text-2xl font-bold text-amber-400">
                  {currentBalance !== null ? currentBalance.toLocaleString() : "—"} coins
                </div>
              </div>
            )}
            <SpinningCoin
              size={success ? 84 : 64}
              spinTrigger={success ? spinTick : (selectedPrice || 0)}
              onClickToSpin={true}
              className="flex-shrink-0"
            />
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Purchase Form */}
          {!success && (
            <>
              {/* Loading Rate */}
              {fetchingRate ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-divine-gold animate-spin mr-2" />
                  <span className="text-white/60">Loading rates...</span>
                </div>
              ) : (
                <>
                  {/* Payment Method Selector — Card vs Affiliate Balance */}
                  <div>
                    <label className="text-white/70 text-sm mb-2 block">
                      Payment Method
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => { setPaymentMethod("card"); setError(""); }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          paymentMethod === "card"
                            ? "border-amber-500 bg-amber-500/10"
                            : "border-white/10 bg-white/5 hover:bg-white/10"
                        }`}
                        data-testid="payment-method-card"
                      >
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-amber-400" />
                          <span className="text-sm text-white font-medium">Card</span>
                        </div>
                        <div className="text-[11px] text-white/50 mt-1">Pay via Stripe</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPaymentMethod("affiliate_balance"); setError(""); }}
                        disabled={availableAffCents <= 0 && !fetchingAff}
                        className={`p-3 rounded-lg border-2 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                          paymentMethod === "affiliate_balance"
                            ? "border-amber-500 bg-amber-500/10"
                            : "border-white/10 bg-white/5 hover:bg-white/10"
                        }`}
                        data-testid="payment-method-affiliate-balance"
                      >
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-amber-400" />
                          <span className="text-sm text-white font-medium">Affiliate Balance</span>
                        </div>
                        <div className="text-[11px] text-white/50 mt-1" data-testid="affiliate-balance-available">
                          {fetchingAff ? "…" : `$${(availableAffCents / 100).toFixed(2)} available`}
                        </div>
                      </button>
                    </div>
                    {paymentMethod === "affiliate_balance" && selectedPrice && (
                      <div className="mt-2 text-[11px] text-white/50 flex items-center justify-between">
                        <span>Balance after purchase:</span>
                        <span className={`font-mono ${availableAffCents >= selectedPrice * 100 ? "text-amber-400" : "text-red-400"}`}>
                          ${((availableAffCents - selectedPrice * 100) / 100).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Price Options */}
                  <div>
                    <label className="text-white/70 text-sm mb-3 block">
                      Select a purchase amount
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {priceOptions.map(({ price, bonus }) => {
                        const { base, total } = getCoinsForPrice(price, bonus);
                        const isSelected = selectedPrice === price;
                        
                        return (
                          <button
                            key={price}
                            onClick={() => handlePriceSelect(price, bonus)}
                            disabled={loading || !usdCrc}
                            className={`
                              relative px-4 py-4 rounded-lg border-2 transition-all
                              ${isSelected 
                                ? 'border-amber-500 bg-amber-500/20' 
                                : 'border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10'
                              }
                              ${(!usdCrc || loading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                            data-testid={`price-btn-${price}`}
                          >
                            {/* Bonus Badge */}
                            {bonus > 0 && (
                              <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-700 text-white text-xs font-bold rounded-full flex items-center gap-1">
                                <Gift className="w-3 h-3" />
                                +{bonus}%
                              </div>
                            )}
                            
                            <div className="flex items-center justify-center gap-1">
                              <DollarSign className={`w-5 h-5 ${isSelected ? 'text-amber-400' : 'text-white/60'}`} />
                              <span className={`text-2xl font-bold ${isSelected ? 'text-amber-400' : 'text-white'}`}>
                                {price}
                              </span>
                            </div>
                            
                            {usdCrc && (
                              <div className="mt-1">
                                {bonus > 0 ? (
                                  <div className="flex flex-col items-center">
                                    <span className="text-xs text-white/40 line-through">
                                      {base.toLocaleString()}
                                    </span>
                                    <span className="text-sm text-green-400 font-medium">
                                      {total.toLocaleString()} coins
                                    </span>
                                  </div>
                                ) : (
                                  <div className="text-xs text-white/40">
                                    {total.toLocaleString()} coins
                                  </div>
                                )}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selected Price Summary */}
                  {selectedPrice && calculatedCoins && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-white/60 text-sm">You will receive</div>
                          <div className="flex items-center gap-2">
                            {bonusPercent > 0 && (
                              <span className="text-lg text-white/40 line-through">
                                {baseCoins?.toLocaleString()}
                              </span>
                            )}
                            <span className="text-2xl font-bold text-amber-400">
                              {calculatedCoins.toLocaleString()} coins
                            </span>
                          </div>
                          {bonusPercent > 0 && (
                            <div className="flex items-center gap-1 text-green-400 text-sm mt-1">
                              <Gift className="w-4 h-4" />
                              <span>+{bonusPercent}% bonus included!</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-white/60 text-sm">Price</div>
                          <div className="text-xl font-bold text-white">
                            ${selectedPrice}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* USD to CRC not configured warning */}
                  {!fetchingRate && !usdCrc && (
                    <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                      <span className="text-yellow-300 text-sm">
                        USD to CRC rate not configured. Please set it in Admin → Tokens.
                      </span>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-white/60 hover:text-white"
            data-testid="coin-purchase-cancel-btn"
          >
            {success ? "Close" : "Cancel"}
          </Button>
          {!success && (
            <>
              {paymentMethod === "affiliate_balance" ? (
                <Button
                  onClick={handleAffiliateBalancePurchase}
                  disabled={payingWithBalance || !selectedPrice || fetchingRate ||
                            (selectedPrice && availableAffCents < selectedPrice * 100)}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                  data-testid="pay-with-affiliate-balance-btn"
                >
                  {payingWithBalance ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Pay ${selectedPrice || "…"} from Affiliate Balance
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleStripeCheckout}
                  disabled={redirecting || loading || !selectedPrice || fetchingRate}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                  data-testid="stripe-checkout-btn"
                >
                  {redirecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to Stripe…
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Pay ${selectedPrice || "…"} with Stripe
                    </>
                  )}
                </Button>
              )}

              {/* ADMIN-ONLY: Legacy TEST PURCHASE path */}
              {(userRole === "admin" || userRole === "support") && (
                <Button
                  onClick={handlePurchase}
                  disabled={loading || redirecting || !calculatedCoins || fetchingRate}
                  variant="outline"
                  className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                  data-testid="test-purchase-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      <Coins className="w-4 h-4 mr-2" />
                      TEST
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CoinPurchaseModal;
